import { CognitoUserPool, CognitoUserSession } from 'amazon-cognito-identity-js';
import React, { useEffect, useRef, useState } from 'react';

import { COGNITO_CLIENT_ID, COGNITO_USER_POOL_ID } from '../../../util/environment';
import type { AuthState, MaybeLoggedIn, UserSession } from '../types';
import {
	cognitoChangePassword,
	cognitoCompleteNewPasswordChallenge,
	cognitoConfirmPassword,
	cognitoForgotPassword,
	cognitoSignIn,
	cognitoSignOut,
	getCognitoUserSession,
} from './util';

export const useCognitoAuth = (): AuthState => {
	if (!COGNITO_USER_POOL_ID || !COGNITO_CLIENT_ID)
		throw new Error(
			'GATSBY_COGNITO_USER_POOL_ID and GATSBY_COGNITO_CLIENT_ID are both required for auth'
		);

	const cognitoUserPool = useRef(
		new CognitoUserPool({
			UserPoolId: COGNITO_USER_POOL_ID,
			ClientId: COGNITO_CLIENT_ID,
		})
	).current;

	const [isLoading, setIsLoading] = useState(false);
	const [sessionValue, setSessionValue] = useState<UserSession | null>(null);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		setIsLoading(true);
		init();
	}, []);

	const init = async () => {
		try {
			const userSession = await getCognitoUserSession(cognitoUserPool);
			if (!userSession) {
				setSessionValue(null);
			} else {
				initialiseUser(userSession);
			}
		} catch (err) {
			console.error('Could not get current session!', err);
			setSessionValue(null);
		}
		setIsLoading(false);
	};

	const logout = () => {
		cognitoSignOut(cognitoUserPool);
		invalidateSession();
	};

	const invalidateSession = () => {
		setSessionValue(null);
	};

	// Initialise the Cognito session from a callback href
	const login = async (email: string, password: string): Promise<MaybeLoggedIn> => {
		setIsLoading(true);
		setError(null);
		try {
			const result = await cognitoSignIn(cognitoUserPool, email, password);
			await initialiseUser(result);
			return { kind: 'loggedIn', error: null };
		} finally {
			setIsLoading(false);
		}
	};

	const resetPassword = async (email: string) => {
		setIsLoading(true);
		setError(null);
		try {
			await cognitoForgotPassword(cognitoUserPool, email);
		} finally {
			setIsLoading(false);
		}
	};

	const changePassword = async (oldPassword: string, newPassword: string) => {
		setIsLoading(true);
		setError(null);
		try {
			await cognitoChangePassword(cognitoUserPool, oldPassword, newPassword);
		} finally {
			setIsLoading(false);
		}
	};

	const confirmNewPassword = async (
		email: string,
		newPassword: string,
		verificationCode: string
	) => {
		setIsLoading(true);
		setError(null);
		try {
			await cognitoConfirmPassword(cognitoUserPool, email, newPassword, verificationCode);
			await cognitoSignIn(cognitoUserPool, email, newPassword);
		} finally {
			setIsLoading(false);
		}
	};

	const completeNewPasswordChallenge = async (newPassword: string) => {
		setIsLoading(true);
		setError(null);
		try {
			await cognitoCompleteNewPasswordChallenge(newPassword);
		} finally {
			setIsLoading(false);
		}
	};

	const initialiseUser = async (session: CognitoUserSession) => {
		const idToken = session.getIdToken().decodePayload();
		const { 'cognito:username': username, email, 'cognito:groups': groups } = idToken;

		// @todo not sure why the type says jwtToken isn't here, because it is!
		const accessToken = (session.getAccessToken() as any).jwtToken as string;

		setSessionValue({
			accessToken,
			username,
			email,
			groups: groups || [],
		});
	};

	return {
		logout,
		login,
		resetPassword,
		confirmNewPassword,
		changePassword,
		error,
		isLoading,
		session: sessionValue,
		invalidateSession,
		completeNewPasswordChallenge,
	};
};
